import { zodResolver } from "@hookform/resolvers/zod";
import { Accordion, ActionIcon, Box, Button, Card, Group, List, Stack, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { InfoIcon } from "@phosphor-icons/react";
import { IconCalendar, IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { DataGridBase } from "@/components/DataGridBase";
import { Input } from "@/components/_Input";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { useDeleteHolidaysByCompanyMutation, useGetHolidaysByCompanyQuery, useSaveHolidaysByCompanyMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/bloqueios")({
  component: RouteComponent
});

const schema = z.object({
  title: z.string().min(1, "O nome do bloqueio é obrigatório"),
  dateRange: z.tuple([z.string(), z.string().nullable()])
});
type Schema = z.infer<typeof schema>;

function RouteComponent() {
  const { colors } = useMantineTheme();
  const { companyId } = useSelectedCompany();
  const { data: holidaysData, isSuccess, isFetching } = useGetHolidaysByCompanyQuery({ id: Number(companyId) }, { skip: !companyId });
  const [saveHolidays] = useSaveHolidaysByCompanyMutation();
  const [deleteHolidays, { isLoading: isDeleting }] = useDeleteHolidaysByCompanyMutation();

  // Desestruturar os dados da API com as propriedades $ (objeto e array)
  const data = holidaysData?.holidays || [];
  const blockedDatesArray = holidaysData?.$blockedDatesArray || [];
  const blockedDatesMap = holidaysData?.$blockedDatesMap || {};

  const today = dayjs().toDate();
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    watch,
    reset
  } = useForm<Schema>({
    defaultValues: {
      title: "",
      dateRange: [undefined, undefined]
    },
    resolver: zodResolver(schema),
    mode: "all"
  });
  const dateRange = watch("dateRange");

  const onSubmit: SubmitHandler<Schema> = async (values) => {
    const { title, dateRange } = values;
    const [startD, endD] = dateRange;

    const startDate = dayjs(startD).toDate();
    const endDate = endD ? dayjs(endD).toDate() : startDate;

    // Gerar array de datas no formato YYYY-MM-DD
    const datesArray: string[] = [];
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);
    while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
      datesArray.push(currentDate.format("YYYY-MM-DD"));
      currentDate = currentDate.add(1, "day");
    }

    await saveHolidays({ company: { id: Number(companyId) }, title, dates: datesArray });
    reset();
  };

  const handleDeleteHoliday = async (id: number) => {
    await deleteHolidays({ id });
  };

  // Função otimizada para verificar se uma data está bloqueada
  // Aceita Date ou string para compatibilidade com o DatePicker do Mantine
  const isDateBlocked = (date: Date | string) => {
    const checkDate = dayjs(date).startOf("day").format("YYYY-MM-DD");
    return blockedDatesArray.includes(checkDate);
  };

  // Função para obter o título do bloqueio de uma data específica
  const getBlockedDateTitle = (date: Date | string) => {
    const checkDate = dayjs(date).startOf("day").format("YYYY-MM-DD");
    return blockedDatesMap[checkDate] || "Data bloqueada";
  };

  type HolidayFromAPI = NonNullable<typeof data>[number];
  const columns: GridColDef<HolidayFromAPI>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "title",
      headerName: "Nome",
      flex: 1
    },
    {
      field: "dates",
      headerName: "Período de Bloqueio",
      width: 250,
      display: "flex",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { row } = params;
        const dates = row.dates;
        const datesArray = dates.map((date) => dayjs(date).format("DD/MM/YYYY"));

        if (dates.length === 0) return <Text>-</Text>;

        return <Text>{datesArray.length === 1 ? datesArray[0] : `${datesArray[0]} até ${datesArray[datesArray.length - 1]}`}</Text>;
      }
    },
    {
      field: "action",
      headerName: "Ações",
      type: "actions",
      width: 110,
      display: "flex",
      renderCell: (params) => (
        <Tooltip label="Deletar">
          <ActionIcon color="red" onClick={() => handleDeleteHoliday(params.row.id)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      )
    }
  ];

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Title order={2}>Bloqueios de Datas</Title>
      </Group>

      {/* Formulário para adicionar novo bloqueio */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <Title order={4}>Adicionar Novo Bloqueio</Title>

            <Group align="flex-center" justify="center" gap="xl">
              {/* Lado esquerdo - Formulário */}
              <Box flex={1} miw={300}>
                <Stack gap="md">
                  <Input.Text
                    name="title"
                    control={control}
                    label="Nome do Bloqueio"
                    placeholder="Ex: Feriado Nacional, Manutenção, etc."
                    leftSection={<IconCalendar size={16} />}
                  />

                  {dateRange[0] && (
                    <Box>
                      <Text size="sm" fw={500} mb="xs">
                        Período Selecionado:
                      </Text>

                      <Text size="sm" c="blue.6">
                        {dayjs(dateRange[0]).format("DD/MM/YYYY")}
                        {dateRange[1] && !dayjs(dateRange[1]).isSame(dayjs(dateRange[0]), "day") ? ` - ${dayjs(dateRange[1]).format("DD/MM/YYYY")}` : ""}
                      </Text>

                      <Text size="sm" c="blue.6" mt="xs">
                        Quantidade de dias: {dateRange[1] ? dayjs(dateRange[1]).diff(dayjs(dateRange[0]), "day") + 1 : 1}
                      </Text>
                    </Box>
                  )}

                  <Group justify="flex-end">
                    <Button type="submit" disabled={!isValid || !isSuccess} loading={isSubmitting || isFetching} leftSection={<IconPlus size={16} />} color="blue">
                      Adicionar Bloqueio
                    </Button>
                  </Group>
                </Stack>
              </Box>

              {/* Lado direito - Calendário */}
              <Box>
                <Text size="sm" fw={500} mb="md">
                  Selecione as datas:
                </Text>

                <Input.DateRange
                  name="dateRange"
                  control={control}
                  numberOfColumns={2}
                  minDate={today}
                  allowSingleDateInRange
                  size="sm"
                  firstDayOfWeek={0}
                  locale="pt-BR"
                  excludeDate={isDateBlocked}
                  showLegend
                  allowMultipleHighlights={false}
                  highlightGroups={[
                    {
                      matcher: (d) => isDateBlocked(d),
                      tooltip: (d) => getBlockedDateTitle(d),
                      variant: "danger",
                      priority: 1,
                      label: "Data bloqueada"
                    }
                  ]}
                />

                <Text size="xs" c="dimmed" mt="xs">
                  • Datas riscadas já estão bloqueadas
                  <br />
                  • Passe o cursor sobre uma data bloqueada para ver o motivo
                  <br />• Selecione uma data ou um período
                </Text>
              </Box>
            </Group>
          </Stack>
        </form>
      </Card>

      {/* Tabela de bloqueios */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Bloqueios Cadastrados</Title>

          <DataGridBase rows={data || []} columns={columns} loading={isFetching || isSubmitting || isDeleting} />
        </Stack>
      </Card>

      <Accordion>
        <Accordion.Item value="default">
          <Accordion.Control icon={<InfoIcon weight="fill" color={colors.blue[5]} />}>Sobre os Bloqueios</Accordion.Control>

          <Accordion.Panel>
            <List type="ordered">
              <List.Item>Os bloqueios impedem que agendamentos sejam feitos nas datas especificadas</List.Item>
              <List.Item>Você pode definir bloqueios para um único dia ou período</List.Item>
              <List.Item>Use para feriados, manutenção de sistema, ou outras situações especiais</List.Item>
              <List.Item>Bloqueios já criados não afetam agendamentos já confirmados</List.Item>
              <List.Item>Datas passadas não podem ser bloqueadas</List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
